import React from "react";
import { size, sumBy } from "lodash";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { AiccFeedbackAbandonmentPoint } from "types/model/aiccCustomer";
import { POST_ACTION_LABEL } from "../constants";
import ChartCard from "./ChartCard";
import TooltipBox from "./TooltipBox";
import { formatAxisDate, formatTooltipDate } from "./formatChartDate";
import {
  AXIS_STROKE,
  AXIS_TICK,
  BAR_MAX_SIZE,
  CHART_COLORS,
  CHART_HEIGHT,
  CHART_MARGIN,
  CHART_SURFACE,
  GRID_STROKE,
  HOVER_CURSOR_FILL,
} from "./chartTheme";
import styles from "./charts.module.scss";

interface Props {
  data: AiccFeedbackAbandonmentPoint[];
  isLoading?: boolean;
}

const SEGMENTS = [
  { dataKey: "continued" as const, name: POST_ACTION_LABEL.continued, color: CHART_COLORS.continued },
  { dataKey: "retried" as const, name: POST_ACTION_LABEL.retried, color: CHART_COLORS.retried },
  { dataKey: "rageQuit" as const, name: POST_ACTION_LABEL.rage_quit, color: CHART_COLORS.rageQuit },
];

function AbandonmentChart({ data, isLoading }: Props) {
  const hasData =
    size(data) > 0 && sumBy(data, ({ continued, retried, rageQuit }) => continued + retried + rageQuit) > 0;

  return (
    <ChartCard
      title="Session Abandonment"
      description="Post-downvote behavior breakdown - daily (total thumbs-down count)"
      isLoading={isLoading}
      hasData={hasData}
    >
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid vertical={false} stroke={GRID_STROKE} />
          <XAxis
            dataKey="date"
            tickFormatter={formatAxisDate}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: AXIS_STROKE }}
            minTickGap={24}
          />
          <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} width={48} />
          <Tooltip
            cursor={{ fill: HOVER_CURSOR_FILL }}
            content={({ active, payload }) => {
              const point = payload?.[0]?.payload as AiccFeedbackAbandonmentPoint | undefined;
              if (!active || !point) {
                return null;
              }

              const total = point.continued + point.retried + point.rageQuit;

              return (
                <TooltipBox
                  title={formatTooltipDate(point.date)}
                  rows={[
                    { label: "Continued", value: String(point.continued), color: CHART_COLORS.continued },
                    { label: "Retried", value: String(point.retried), color: CHART_COLORS.retried },
                    { label: "Rage-Quit", value: String(point.rageQuit), color: CHART_COLORS.rageQuit },
                    { label: "Total 👎 Thumbs Down", value: String(total) },
                  ]}
                />
              );
            }}
          />
          <Legend
            iconType="square"
            iconSize={10}
            height={48}
            formatter={(value) => <span className={styles.legendLabel}>{value}</span>}
            wrapperStyle={{ fontSize: 12, paddingTop: 8, lineHeight: "20px" }}
          />
          {SEGMENTS.map(({ dataKey, name, color }) => (
            <Bar
              key={dataKey}
              dataKey={dataKey}
              name={name}
              stackId="downvotes"
              fill={color}
              maxBarSize={BAR_MAX_SIZE}
              stroke={CHART_SURFACE}
              strokeWidth={2}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export default AbandonmentChart;
